import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import { faCopy } from '@fortawesome/pro-light-svg-icons/faCopy';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-light-svg-icons/faArrowUpRightFromSquare';
import { TypedMemo } from '../../../common/types';
import { LoadingView } from '../../../common/views/LoadingView';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { useS4aPitch } from '../hooks/useS4aPitch';
import type { ReleaseOption } from '../hooks/useReleaseOptions';
import { findMessages as messages } from './findMessages';
import { S4aPitchedMark } from './S4aPitchedMark';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
import styles from './Find.module.scss';
/* eslint-enable css-modules/no-unused-class */

const S4A_URL = 'https://artists.spotify.com/';

/** True when the song is out: Spotify takes editorial pitches only for unreleased songs. */
export function isReleased(releaseDate: string | null, now: number = Date.now()): boolean {
  return releaseDate !== null && Date.parse(releaseDate) <= now;
}

interface S4aHelperModalProps {
  accountId: number;
  song: ReleaseOption | null;
  onClose: () => void;
}

/**
 * The Spotify for Artists pitch helper (design outreach m3, prompt P8): the 500-character pitch and
 * the form fields, to copy into Spotify for Artists. Spotify has no pitch API: the artist pastes it.
 */
const S4aHelperModalComponent: React.FC<S4aHelperModalProps> = ({ accountId, song, onClose }) => {
  const intl = useIntl();
  const { write, pitch, isWriting, error } = useS4aPitch(accountId);
  const [text, setText] = useState('');
  const released = song !== null && isReleased(song.releaseDate);
  const songUuid = song?.catalogItemUuid ?? null;

  useEffect(() => {
    if (songUuid !== null && !released) {
      write(songUuid);
    }
  }, [released, songUuid, write]);
  useEffect(() => {
    if (pitch !== undefined) {
      setText(pitch.pitch);
    }
  }, [pitch]);

  const fields = useMemo(
    () =>
      pitch === undefined
        ? []
        : [
            {
              key: 'genres',
              text: intl.formatMessage(messages.s4aGenres, { list: pitch.genres.join(', ') }),
              show: pitch.genres.length > 0,
            },
            {
              key: 'moods',
              text: intl.formatMessage(messages.s4aMoods, { list: pitch.moods.join(', ') }),
              show: pitch.moods.length > 0,
            },
            {
              key: 'instruments',
              text: intl.formatMessage(messages.s4aInstruments, { list: pitch.instruments.join(', ') }),
              show: pitch.instruments.length > 0,
            },
            {
              key: 'culture',
              text: intl.formatMessage(messages.s4aCulture, { list: pitch.culture.join(', ') }),
              show: pitch.culture.length > 0,
            },
          ]
            .filter((field) => field.show)
            .map((field) => (
              <span key={field.key} className={shared.chip}>
                {field.text}
              </span>
            )),
    [intl, pitch]
  );

  const body = () => {
    if (song === null) {
      return <p className={shared.sub}>{intl.formatMessage(messages.s4aPickSong)}</p>;
    }
    if (released) {
      return <p className={shared.sub}>{intl.formatMessage(messages.s4aReleased)}</p>;
    }
    if (isWriting) {
      return <LoadingView />;
    }
    if (error !== null) {
      return <p className="text-danger mb-0">{intl.formatMessage(messages.s4aFailed)}</p>;
    }
    return (
      <>
        <label className={shared.label} htmlFor="s4a-pitch">
          {intl.formatMessage(messages.s4aPitchLabel)}
        </label>
        <textarea
          id="s4a-pitch"
          className={`${shared.textarea} ${styles.s4aText}`}
          maxLength={500}
          value={text}
          onChange={(event) => setText(event.target.value)}
        />
        <span className={styles.count}>{intl.formatMessage(messages.s4aCount, { count: text.length })}</span>
        <div className={shared.row}>{fields}</div>
        <div className={`${shared.row} justify-content-end`}>
          <SMLButton
            outline
            omitCap
            leftIcon={faArrowUpRightFromSquare}
            onClick={() => window.open(S4A_URL, '_blank', 'noopener')}
          >
            {intl.formatMessage(messages.s4aOpen)}
          </SMLButton>
          <SMLButton
            color="primary"
            omitCap
            leftIcon={faCopy}
            onClick={() => navigator.clipboard.writeText(text).catch(console.error)}
          >
            {intl.formatMessage(messages.s4aCopy)}
          </SMLButton>
        </div>
        <p className={shared.small}>{intl.formatMessage(messages.s4aNoApi)}</p>
      </>
    );
  };

  return (
    <Modal isOpen toggle={onClose} centered size="lg">
      <ModalHeader toggle={onClose}>{intl.formatMessage(messages.s4aTitle, { song: song?.title ?? '' })}</ModalHeader>
      <ModalBody className="d-flex flex-column gap-3" data-testid="s4a-helper">
        <p className={shared.sub}>{intl.formatMessage(messages.s4aBody)}</p>
        {body()}
        {song !== null && !released && <S4aPitchedMark accountId={accountId} catalogItemUuid={song.catalogItemUuid} />}
      </ModalBody>
    </Modal>
  );
};

export const S4aHelperModal = TypedMemo(S4aHelperModalComponent);
